import { useInitialLoading } from '../hooks/useInitialLoading';
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAuthHeaders } from '../utils/authHeaders';

export default function ProjectDirectory() {
  const navigate = useNavigate();
  const companyId = localStorage.getItem('selectedCompanyId');
  const API = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

  const [projects, setProjects] = useState([]);
  const [pagination, setPagination] = useState({});
  const [loading, setLoading] = useState(true);
  const initialLoading = useInitialLoading(loading);
  const [projectTypes, setProjectTypes] = useState([]);
  const [filters, setFilters] = useState({ status: '', health: '', priority: '', project_type_id: '', search: '', page: 1 });
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({ name: '', project_type_id: '', client_name: '', priority: 'normal', description: '', planned_start_date: '', target_end_date: '' });
  const [editId, setEditId] = useState(null);
  const [selectedProject, setSelectedProject] = useState(null);
  const [statusForm, setStatusForm] = useState({ status: '', reason: '' });
  const [showStatusModal, setShowStatusModal] = useState(false);

  const loadProjects = useCallback(() => {
    if (!companyId) return;
    setLoading(true);
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => { if (v) params.append(k, v); });
    fetch(`${API}/companies/${companyId}/projects?${params}`, { headers: getAuthHeaders() })
      .then(r => { if (!r.ok) throw new Error(); return r.json(); })
      .then(d => { setProjects(Array.isArray(d.data) ? d.data : []); setPagination(d); })
      .catch(() => setProjects([]))
      .finally(() => setLoading(false));
  }, [companyId, API, filters]);

  useEffect(() => { loadProjects(); }, [loadProjects]);

  useEffect(() => {
    if (!companyId) return;
    fetch(`${API}/companies/${companyId}/projects-settings/types`, { headers: getAuthHeaders() })
      .then(r => { if (!r.ok) throw new Error(); return r.json(); })
      .then(d => setProjectTypes(Array.isArray(d) ? d : []))
      .catch(() => {});
  }, [companyId, API]);

  const handleSave = () => {
    const method = editId ? 'PUT' : 'POST';
    const url = editId ? `${API}/companies/${companyId}/projects/${editId}` : `${API}/companies/${companyId}/projects`;
    fetch(url, {
      method,
      headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify(form),
    })
      .then(r => { if (!r.ok) throw new Error(); return r.json(); })
      .then(() => { setShowModal(false); setEditId(null); setForm({ name: '', project_type_id: '', client_name: '', priority: 'normal', description: '', planned_start_date: '', target_end_date: '' }); loadProjects(); })
      .catch(() => window.alert('Failed to save project'));
  };

  const openEdit = (p) => {
    setEditId(p.id);
    setForm({ name: p.name, project_type_id: p.project_type_id || '', client_name: p.client_name || '', priority: p.priority, description: p.description || '', planned_start_date: p.planned_start_date || '', target_end_date: p.target_end_date || '' });
    setShowModal(true);
  };

  const handleChangeStatus = () => {
    fetch(`${API}/companies/${companyId}/projects/${selectedProject.id}/status`, {
      method: 'POST',
      headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify(statusForm),
    })
      .then(r => { if (!r.ok) throw new Error(); return r.json(); })
      .then(() => { setShowStatusModal(false); loadProjects(); })
      .catch(() => window.alert('Failed to change status'));
  };

  const handleDelete = (p) => {
    if (!window.confirm(`Delete project "${p.name}"? Only draft projects can be deleted.`)) return;
    fetch(`${API}/companies/${companyId}/projects/${p.id}`, { method: 'DELETE', headers: getAuthHeaders() })
      .then(r => { if (!r.ok) return r.json().then(d => { throw new Error(d.message); }); return r.json(); })
      .then(() => loadProjects())
      .catch(e => window.alert(e.message || 'Failed to delete'));
  };

  const healthColor = { on_track: '#22c55e', at_risk: '#f59e0b', delayed: '#ef4444', critical: '#dc2626' };
  const statusColor = { draft: '#94a3b8', planned: '#3b82f6', active: '#10b981', on_hold: '#f59e0b', completed: '#8b5cf6', cancelled: '#ef4444', archived: '#6b7280' };
  const priorityColor = { low: '#6b7280', normal: '#3b82f6', high: '#f59e0b', critical: '#ef4444' };

  if (!companyId) return <div style={pageStyle}><p>Please select a company first.</p></div>;

  return (
    <div style={pageStyle}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h2 style={{ margin: 0, fontSize: '1.5rem', fontWeight: 700 }}>📁 Project Directory</h2>
        <button onClick={() => { setEditId(null); setForm({ name: '', project_type_id: '', client_name: '', priority: 'normal', description: '', planned_start_date: '', target_end_date: '' }); setShowModal(true); }} style={primaryBtnStyle}>+ New Project</button>
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: '10px', marginBottom: '16px', flexWrap: 'wrap' }}>
        <input placeholder="Search projects..." value={filters.search} onChange={e => setFilters({ ...filters, search: e.target.value, page: 1 })} style={inputStyle} />
        <select value={filters.status} onChange={e => setFilters({ ...filters, status: e.target.value, page: 1 })} style={selectStyle}>
          <option value="">All Statuses</option>
          {['draft','planned','active','on_hold','completed','cancelled'].map(s => <option key={s} value={s}>{s.replace(/_/g,' ')}</option>)}
        </select>
        <select value={filters.health} onChange={e => setFilters({ ...filters, health: e.target.value, page: 1 })} style={selectStyle}>
          <option value="">All Health</option>
          {['on_track','at_risk','delayed','critical'].map(h => <option key={h} value={h}>{h.replace(/_/g,' ')}</option>)}
        </select>
        <select value={filters.priority} onChange={e => setFilters({ ...filters, priority: e.target.value, page: 1 })} style={selectStyle}>
          <option value="">All Priorities</option>
          {['low','normal','high','critical'].map(p => <option key={p} value={p}>{p}</option>)}
        </select>
        <select value={filters.project_type_id} onChange={e => setFilters({ ...filters, project_type_id: e.target.value, page: 1 })} style={selectStyle}>
          <option value="">All Types</option>
          {projectTypes.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
        </select>
      </div>

      {/* Table */}
      {initialLoading ? <p>Loading...</p> : (
        <div style={{ ...cardStyle, padding: '0', overflowX: 'auto' }}>
          <table style={tableStyle}>
            <thead>
              <tr>
                <th style={thStyle}>Project</th>
                <th style={thStyle}>Client</th>
                <th style={thStyle}>Type</th>
                <th style={thStyle}>Manager</th>
                <th style={thStyle}>Status</th>
                <th style={thStyle}>Health</th>
                <th style={thStyle}>Priority</th>
                <th style={thStyle}>Progress</th>
                <th style={thStyle}>Tasks</th>
                <th style={thStyle}>End Date</th>
                <th style={thStyle}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {projects.map(p => (
                <tr key={p.id}>
                  <td style={tdStyle}>
                    <div style={{ fontWeight: 600, cursor: 'pointer', color: '#3b82f6' }} onClick={() => navigate(`/company/projects/${p.id}`)}>{p.name}</div>
                    <div style={{ fontSize: '0.7rem', color: '#94a3b8' }}>{p.project_code}</div>
                  </td>
                  <td style={tdStyle}>{p.client_name || '-'}</td>
                  <td style={tdStyle}>{p.project_type?.name || '-'}</td>
                  <td style={tdStyle}>{p.project_manager?.name || '-'}</td>
                  <td style={tdStyle}><span style={{ ...badgeStyle, backgroundColor: statusColor[p.status] || '#6b7280' }}>{(p.status || '').replace(/_/g,' ')}</span></td>
                  <td style={tdStyle}><span style={{ ...badgeStyle, backgroundColor: healthColor[p.health] || '#6b7280' }}>{(p.health || '').replace(/_/g,' ')}</span></td>
                  <td style={tdStyle}><span style={{ ...badgeStyle, backgroundColor: priorityColor[p.priority] || '#6b7280' }}>{p.priority}</span></td>
                  <td style={tdStyle}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <div style={{ width: '50px', height: '6px', backgroundColor: '#e2e8f0', borderRadius: '3px' }}>
                        <div style={{ width: `${p.progress_percentage || 0}%`, height: '100%', backgroundColor: '#3b82f6', borderRadius: '3px' }} />
                      </div>
                      <span style={{ fontSize: '0.75rem' }}>{p.progress_percentage || 0}%</span>
                    </div>
                  </td>
                  <td style={tdStyle}>
                    <span>{p.open_tasks_count || 0}</span>
                    {(p.overdue_tasks_count || 0) > 0 && <span style={{ color: '#ef4444', marginLeft: '4px' }}>({p.overdue_tasks_count} overdue)</span>}
                  </td>
                  <td style={tdStyle}>{p.target_end_date || '-'}</td>
                  <td style={tdStyle}>
                    <div style={{ display: 'flex', gap: '4px' }}>
                      <button onClick={() => openEdit(p)} style={smallBtnStyle}>Edit</button>
                      <button onClick={() => { setSelectedProject(p); setStatusForm({ status: '', reason: '' }); setShowStatusModal(true); }} style={smallBtnStyle}>Status</button>
                      {p.status === 'draft' && <button onClick={() => handleDelete(p)} style={{ ...smallBtnStyle, color: '#ef4444' }}>Delete</button>}
                    </div>
                  </td>
                </tr>
              ))}
              {projects.length === 0 && <tr><td colSpan="11" style={{ ...tdStyle, textAlign: 'center', color: '#94a3b8' }}>No projects found</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      {pagination.last_page > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', gap: '8px', marginTop: '16px' }}>
          {Array.from({ length: pagination.last_page }, (_, i) => (
            <button key={i} onClick={() => setFilters({ ...filters, page: i + 1 })} style={{ ...smallBtnStyle, backgroundColor: filters.page === i + 1 ? '#3b82f6' : '#f1f5f9', color: filters.page === i + 1 ? '#fff' : '#334155' }}>{i + 1}</button>
          ))}
        </div>
      )}

      {/* Create/Edit Modal */}
      {showModal && (
        <div style={overlayStyle}>
          <div style={modalStyle}>
            <h3 style={{ margin: '0 0 16px' }}>{editId ? 'Edit Project' : 'Create Project'}</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              <input placeholder="Project Name *" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} style={inputStyle} />
              <select value={form.project_type_id} onChange={e => setForm({ ...form, project_type_id: e.target.value })} style={selectStyle}>
                <option value="">Select Type</option>
                {projectTypes.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
              <input placeholder="Client Name" value={form.client_name} onChange={e => setForm({ ...form, client_name: e.target.value })} style={inputStyle} />
              <select value={form.priority} onChange={e => setForm({ ...form, priority: e.target.value })} style={selectStyle}>
                {['low','normal','high','critical'].map(p => <option key={p} value={p}>{p}</option>)}
              </select>
              <div style={{ display: 'flex', gap: '10px' }}>
                <div style={{ flex: 1 }}>
                  <label style={labelStyle}>Start Date</label>
                  <input type="date" value={form.planned_start_date} onChange={e => setForm({ ...form, planned_start_date: e.target.value })} style={inputStyle} />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={labelStyle}>Target End Date</label>
                  <input type="date" value={form.target_end_date} onChange={e => setForm({ ...form, target_end_date: e.target.value })} style={inputStyle} />
                </div>
              </div>
              <textarea placeholder="Description" value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} style={{ ...inputStyle, minHeight: '60px' }} />
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '16px' }}>
              <button onClick={() => setShowModal(false)} style={cancelBtnStyle}>Cancel</button>
              <button onClick={handleSave} style={primaryBtnStyle}>Save</button>
            </div>
          </div>
        </div>
      )}

      {/* Status Modal */}
      {showStatusModal && selectedProject && (
        <div style={overlayStyle}>
          <div style={modalStyle}>
            <h3 style={{ margin: '0 0 16px' }}>Change Status — {selectedProject.name}</h3>
            <p style={{ fontSize: '0.85rem', color: '#64748b' }}>Current: <strong>{(selectedProject.status || '').replace(/_/g, ' ')}</strong></p>
            <select value={statusForm.status} onChange={e => setStatusForm({ ...statusForm, status: e.target.value })} style={selectStyle}>
              <option value="">Select New Status</option>
              {['draft','planned','active','on_hold','completed','cancelled','archived'].map(s => <option key={s} value={s}>{s.replace(/_/g,' ')}</option>)}
            </select>
            {['on_hold','cancelled'].includes(statusForm.status) && (
              <textarea placeholder="Reason" value={statusForm.reason} onChange={e => setStatusForm({ ...statusForm, reason: e.target.value })} style={{ ...inputStyle, marginTop: '10px', minHeight: '60px' }} />
            )}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '16px' }}>
              <button onClick={() => setShowStatusModal(false)} style={cancelBtnStyle}>Cancel</button>
              <button onClick={handleChangeStatus} disabled={!statusForm.status} style={primaryBtnStyle}>Update</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const pageStyle = { padding: '24px', maxWidth: '1400px', margin: '0 auto' };
const cardStyle = { backgroundColor: '#fff', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)', border: '1px solid #e2e8f0' };
const tableStyle = { width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem' };
const thStyle = { textAlign: 'left', padding: '8px 10px', borderBottom: '2px solid #e2e8f0', fontSize: '0.72rem', fontWeight: 600, color: '#64748b', textTransform: 'uppercase', whiteSpace: 'nowrap' };
const tdStyle = { padding: '8px 10px', borderBottom: '1px solid #f1f5f9', fontSize: '0.82rem' };
const badgeStyle = { display: 'inline-block', padding: '2px 8px', borderRadius: '10px', fontSize: '0.68rem', color: '#fff', fontWeight: 600, textTransform: 'capitalize' };
const inputStyle = { padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '0.85rem', width: '100%', boxSizing: 'border-box' };
const selectStyle = { padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '0.85rem', backgroundColor: '#fff' };
const labelStyle = { fontSize: '0.75rem', fontWeight: 600, color: '#64748b', marginBottom: '4px', display: 'block' };
const primaryBtnStyle = { padding: '8px 16px', backgroundColor: '#3b82f6', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 600, fontSize: '0.85rem' };
const cancelBtnStyle = { padding: '8px 16px', backgroundColor: '#f1f5f9', color: '#334155', border: '1px solid #d1d5db', borderRadius: '6px', cursor: 'pointer', fontSize: '0.85rem' };
const smallBtnStyle = { padding: '4px 8px', backgroundColor: '#f1f5f9', color: '#334155', border: '1px solid #e2e8f0', borderRadius: '4px', cursor: 'pointer', fontSize: '0.75rem' };
const overlayStyle = { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 };
const modalStyle = { backgroundColor: '#fff', borderRadius: '12px', padding: '24px', width: '90%', maxWidth: '500px', maxHeight: '80vh', overflowY: 'auto' };
